import React, { useEffect, useState } from "react";
import api from "../utils/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import he from "he";
import parse from "html-react-parser";
import {
    FiBook,
    FiUsers,
    FiCheckCircle,
    FiXCircle,
    FiHelpCircle,
    FiAward,
    FiAlertTriangle,
    FiArrowLeft,
    FiChevronDown,
    FiChevronRight,
    FiBarChart2,
    FiFileText,
    FiUser
} from 'react-icons/fi';

const ChapterAnalysis = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const subjectName = location.state?.subjectName || "Subject";
    const lastPapers = location.state?.lastPapers;
    const paperId = location.state?.paperId;
    const isLastPapersMode = !!lastPapers;

    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("chapters");
    const [questions, setQuestions] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [expandedChapters, setExpandedChapters] = useState({});
    const [topicsByChapter, setTopicsByChapter] = useState({});
    const [topicsLoading, setTopicsLoading] = useState({});
    const [studentTab, setStudentTab] = useState("top");
    const [topStudents, setTopStudents] = useState([]);
    const [weakStudents, setWeakStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);

    useEffect(() => {
        fetchData();
        fetchQuestions();
        fetchTopAndWeakStudents();
    }, [id, lastPapers, paperId]);

    const renderLatexText = (text, layout = "single", fontSize = "16px") => {
        if (!text) return null;

        let decodedText = he.decode(text);
        decodedText = decodedText.replace(/<br\s*\/?>/gi, "\n");

        const latexAndImageRegex =
            /\\\((.*?)\\\)|\\\[(.*?)\\\]|<img[^>]*\bsrc=(["'])(.*?)\3[^>]*>|\\begin{align}([\s\S]*?)\\end{align}/g;

        const parts = [];
        let lastIndex = 0;
        let match;
        let key = 0;

        while ((match = latexAndImageRegex.exec(decodedText)) !== null) {
            const [fullMatch, inlineLatex, blockLatex, , imageSrc, alignContent] =
                match;
            const matchIndex = match.index;

            if (matchIndex > lastIndex) {
                const plainText = decodedText.slice(lastIndex, matchIndex);
                plainText.split("\n").forEach((line, i, arr) => {
                    if (line) parts.push(<span key={`html-${key++}`}>{parse(line)}</span>);
                    if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
                });
            }

            if (imageSrc) {
                parts.push(
                    <img
                        key={`img-${key++}`}
                        src={imageSrc}
                        alt="Image"
                        style={{
                            maxWidth: `calc(${fontSize} * 18)`,
                            display: "inline",
                            verticalAlign: "middle",
                            marginLeft: "4px",
                            marginRight: "4px",
                        }}
                    />
                );
            } else if (alignContent) {
                parts.push(
                    <BlockMath key={`align-${key++}`}>
                        {`\\begin{align}${alignContent}\\end{align}`}
                    </BlockMath>
                );
            } else if (inlineLatex || blockLatex) {
                parts.push(
                    <InlineMath key={`inline-${key++}`}>
                        {inlineLatex || blockLatex}
                    </InlineMath>
                );
            }

            lastIndex = matchIndex + fullMatch.length;
        }

        if (lastIndex < decodedText.length) {
            const remaining = decodedText.slice(lastIndex);
            remaining.split("\n").forEach((line, i, arr) => {
                if (line) parts.push(<span className="normal-inline" key={`html-${key++}`}>{parse(line)}</span>);
                if (i < arr.length - 1) parts.push(<br key={`br-${key++}`} />);
            });
        }

        return <span style={{ whiteSpace: "normal", display: "inline" }}>{parts}</span>;
    };

    async function fetchData() {
        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "getChapterWiseStatsWithLimit");
            fd.append("subjectId", id);

            if (isLastPapersMode) {
                fd.append("lastPapers", lastPapers);
            } else {
                fd.append("paperId", paperId);
            }

            const res = await api.post("overView/overView2", fd);
            console.log(res);
            setChapters(res.data.chapterWiseCombined || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    async function fetchTopAndWeakStudents() {
        setStudentsLoading(true);
        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "getTopAndWeakStudentsForSubject");
            fd.append("subjectId", id);

            if (isLastPapersMode) {
                fd.append("lastPapers", lastPapers);
            } else {
                fd.append("paperId", paperId);
            }

            const res = await api.post("overView/overView2", fd);
            if (res.data?.status === "success") {
                setTopStudents(res.data.topStudents || []);
                setWeakStudents(res.data.weakStudents || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setStudentsLoading(false);
        }
    }

    async function fetchTopics(chapterId) {
        if (topicsByChapter[chapterId]) return;

        setTopicsLoading(prev => ({ ...prev, [chapterId]: true }));

        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "getTopicWiseStatsWithLimit");
            fd.append("subjectId", id);
            fd.append("chapterId", chapterId);

            if (isLastPapersMode) {
                fd.append("lastPapers", lastPapers);
            } else {
                fd.append("paperId", paperId);
            }

            const res = await api.post("overView/overView2", fd);
            setTopicsByChapter(prev => ({
                ...prev,
                [chapterId]: res.data?.topicWiseCombined || []
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setTopicsLoading(prev => ({ ...prev, [chapterId]: false }));
        }
    }

    const toggleChapter = (chapterId) => {
        setExpandedChapters(prev => {
            const isOpen = !!prev[chapterId];
            if (!isOpen) {
                fetchTopics(chapterId);
            }
            return {
                ...prev,
                [chapterId]: !isOpen
            };
        });
    };

    async function fetchQuestions() {
        setQuestionsLoading(true);
        try {
            const fd = new FormData();
            fd.append("loggedInUser", localStorage.getItem("loggedInUser"));
            fd.append("token", localStorage.getItem("authToken"));
            fd.append("programType", "getSubjectWiseQuestionsWithStats");
            fd.append("subjectId", id);

            if (isLastPapersMode) {
                fd.append("lastPapers", lastPapers);
            } else {
                fd.append("paperId", paperId);
            }

            const res = await api.post("overView/overView2", fd);
            console.log("Questions response:", res);
            setQuestions(res.data?.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setQuestionsLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="analysis-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Loading Subject Analysis...</div>
                </div>
            </div>
        );
    }

    const optionLabels = ["A", "B", "C", "D"];

    return (
        <div className="analysis-container">


            {/* Header */}
            <div className="analysis-header">
                <div className="header-content">
                    <h1> {subjectName}</h1>
                    <p>Detailed subject analysis and performance metrics</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="tab-navigation">
                <button
                    className={`tab-button ${activeTab === "chapters" ? "active" : ""}`}
                    onClick={() => setActiveTab("chapters")}
                >
                    <FiBook size={16} />
                    Chapter Analysis
                </button>
                <button
                    className={`tab-button ${activeTab === "questions" ? "active" : ""}`}
                    onClick={() => setActiveTab("questions")}
                >
                    <FiFileText size={16} />
                    Questions
                </button>
                <button
                    className={`tab-button ${activeTab === "students" ? "active" : ""}`}
                    onClick={() => setActiveTab("students")}
                >
                    <FiUsers size={16} />
                    Students
                </button>
            </div>

            {/* Chapter Analysis Tab */}
            {activeTab === "chapters" && (
                <div className="chapters-section">
                    <div className="section-header">
                        <h2>Chapter Performance</h2>
                        <p>Performance breakdown by chapters</p>
                    </div>

                    <div className="chapters-grid">
                        {chapters.map((ch) => (
                            <div key={ch.chapterId} className="chapter-card">
                                <div
                                    className="chapter-header"
                                    onClick={() => toggleChapter(ch.chapterId)}
                                >
                                    <div className="chapter-title">
                                        <div className="chapter-icon">
                                            <FiBook size={20} />
                                        </div>
                                        <div>
                                            <h3>{ch.chapterName}</h3>
                                            <div className="chapter-meta">
                                                <span>{ch.totalQuestions} Questions</span>
                                                <span>•</span>
                                                <span>{ch.totalPapers} Papers</span>
                                                <span>•</span>
                                                <span>{ch.totalStudents} Students</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="chapter-toggle">
                                        {expandedChapters[ch.chapterId] ?
                                            <FiChevronDown size={20} /> :
                                            <FiChevronRight size={20} />
                                        }
                                    </div>
                                </div>

                                <div className="chapter-stats">
                                    <div className="stat-item">
                                        <FiCheckCircle size={16} color="#16a34a" />
                                        <div>
                                            <div className="stat-label">Correct</div>
                                            <div className="stat-value text-green-600">
                                                {ch.correctPercentage}%
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stat-item">
                                        <FiXCircle size={16} color="#dc2626" />
                                        <div>
                                            <div className="stat-label">Wrong</div>
                                            <div className="stat-value text-red-600">
                                                {ch.wrongPercentage}%
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stat-item">
                                        <FiHelpCircle size={16} color="#6b7280" />
                                        <div>
                                            <div className="stat-label">Unanswered</div>
                                            <div className="stat-value text-gray-600">
                                                {ch.unansweredPercentage}%
                                            </div>
                                        </div>
                                    </div>
                                    <div className="stat-item">
                                        <FiBarChart2 size={16} color="#0369a1" />
                                        <div>
                                            <div className="stat-label">Accuracy</div>
                                            <div
                                                className="stat-value"
                                                style={{
                                                    color:
                                                        ch.averageAccuracy >= 70
                                                            ? "#16a34a"
                                                            : ch.averageAccuracy >= 50
                                                                ? "#eab308"
                                                                : "#dc2626"
                                                }}
                                            >
                                                {ch.averageAccuracy}%
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                {/* Topics Section */}
                                {expandedChapters[ch.chapterId] && (
                                    <div className="topics-section">
                                        <div className="topics-header">Topics</div>
                                        {topicsLoading[ch.chapterId] ? (
                                            <div className="loading-topics">Loading topics...</div>
                                        ) : topicsByChapter[ch.chapterId]?.length > 0 ? (
                                            <div className="topics-grid">
                                                {topicsByChapter[ch.chapterId].map(topic => (
                                                    <div key={topic.topicId} className="topic-card">
                                                        <div className="topic-name">{topic.topicName}</div>
                                                        <div className="topic-stats">
                                                            <div className="topic-stat">
                                                                <span>Questions:</span>
                                                                <strong>{topic.totalQuestions}</strong>
                                                            </div>
                                                            <div className="topic-stat">
                                                                <span>Correct:</span>
                                                                <strong className="text-green-600">{topic.totalCorrect}</strong>
                                                            </div>
                                                            <div className="topic-stat">
                                                                <span>Wrong:</span>
                                                                <strong className="text-red-600">{topic.totalWrong}</strong>
                                                            </div>
                                                            <div className="topic-stat">
                                                                <span>Accuracy:</span>
                                                                <strong className="text-blue-600">{topic.averageAccuracy}%</strong>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="no-topics">No topics available</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {chapters.length === 0 && (
                            <div className="empty-state">
                                <FiBook size={32} />
                                <p>No chapter data available</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Questions Tab */}
            {activeTab === "questions" && (
                <div className="questions-section">
                    <div className="section-header">
                        <h2>Questions Analysis</h2>
                        <p>Detailed question-wise performance</p>
                    </div>

                    {questionsLoading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading questions...</p>
                        </div>
                    ) : (
                        <div className="questions-grid">
                            {questions.map((q, index) => (
                                <div key={q.questionId} className="question-card">
                                    <div className="question-header">
                                        <div className="question-number">Q{index + 1}</div>
                                        <div className="question-stats">
                                            <Badge icon={<FiCheckCircle />} label="Correct" value={q.totalCorrect} color="#16a34a" bg="#dcfce7" />
                                            <Badge icon={<FiXCircle />} label="Wrong" value={q.totalWrong} color="#dc2626" bg="#fee2e2" />
                                            <Badge icon={<FiHelpCircle />} label="Unanswered" value={q.totalUnanswered} color="#6b7280" bg="#f3f4f6" />
                                            <Badge icon={<FiBarChart2 />} label="Accuracy" value={`${q.accuracyPercentage}%`} color="#0369a1" bg="#e0f2fe" />
                                        </div>
                                    </div>

                                    <div className="question-content">
                                        {renderLatexText(q.question)}
                                    </div>

                                    <div className="options-grid">
                                        {q.options?.map((opt, i) => {
                                            const isCorrect = opt.id === q.correctAnswerId;
                                            return (
                                                <div
                                                    key={opt.id}
                                                    className={`option-card ${isCorrect ? 'correct' : ''}`}
                                                >
                                                    <div className="option-header">
                                                        <div className="option-letter">{optionLabels[i]}</div>
                                                        {isCorrect && (
                                                            <div className="correct-badge">
                                                                <FiCheckCircle size={12} />
                                                                Correct
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="option-content">
                                                        {renderLatexText(opt.text)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {questions.length === 0 && (
                                <div className="empty-state">
                                    <FiFileText size={32} />
                                    <p>No questions available</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Students Tab */}
            {activeTab === "students" && (
                <div className="students-section">
                    <div className="section-header">
                        <h2>Student Performance</h2>
                        <p>Top and weak performers in this subject</p>
                    </div>

                    <div className="student-tabs">
                        <button
                            className={`student-tab ${studentTab === "top" ? "active" : ""}`}
                            onClick={() => setStudentTab("top")}
                        >
                            <FiAward size={16} />
                            Top Performers
                        </button>
                        <button
                            className={`student-tab ${studentTab === "weak" ? "active" : ""}`}
                            onClick={() => setStudentTab("weak")}
                        >
                            <FiAlertTriangle size={16} />
                            Weak Performers
                        </button>
                    </div>

                    {studentsLoading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading students...</p>
                        </div>
                    ) : (
                        <div className="students-table-container">
                            <table className="students-table">
                                <thead>
                                    <tr>
                                        <th>Rank</th>
                                        <th>Student Name</th>
                                        <th>Roll No</th>
                                        <th>Correct</th>
                                        <th>Wrong</th>
                                        <th>Accuracy</th>
                                        <th>Marks</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(studentTab === "top" ? topStudents : weakStudents).map((s, i) => (
                                        <tr key={s.studentId}>
                                            <td>
                                                <div className={`rank-badge ${studentTab === "top" ? "top-rank" : "weak-rank"}`}>
                                                    {i + 1}
                                                </div>
                                            </td>
                                            <td className="student-name-cell">
                                                <div className="student-avatar">
                                                    <FiUser size={14} />
                                                </div>
                                                {s.name}
                                            </td>
                                            <td className="roll-number">{s.rollNumber}</td>
                                            <td className="text-green-600">{s.totalCorrect}</td>
                                            <td className="text-red-600">{s.totalWrong}</td>
                                            <td>
                                                <div className={`accuracy-badge ${s.accuracyPercentage >= 60 ? "high-accuracy" : "low-accuracy"
                                                    }`}>
                                                    {s.accuracyPercentage}%
                                                </div>
                                            </td>
                                            <td className="marks-display">{s.totalMarks}</td>
                                            <td>
                                                <button
                                                    className="view-student-button"
                                                    onClick={() => navigate(`/student-overall/${s.studentId}`)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {(studentTab === "top" ? topStudents : weakStudents).length === 0 && (
                                <div className="empty-state">
                                    <FiUsers size={32} />
                                    <p>No students found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <style>
                {`
                    .analysis-container {
                        min-height: 100vh;
                        background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
                        padding: 1.5rem;
                    }

                    /* Loading States */
                    .loading-container, .loading-state {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        min-height: 400px;
                    }

                    .loading-spinner {
                        width: 40px;
                        height: 40px;
                        border: 3px solid #e0f2fe;
                        border-top: 3px solid #0369a1;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }

                    .loading-text {
                        margin-top: 12px;
                        color: #64748b;
                        font-size: 0.875rem;
                    }

                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }

                   

                    /* Header */
                    .analysis-header {
                       
                        margin: 0 auto 2rem;
                        background: white;
                        border-radius: 1rem;
                        padding: 1.5rem;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                        border: 1px solid #e2e8f0;
                    }

                    .header-content h1 {
                        font-size: 1.75rem;
                        font-weight: 700;
                        color: #1f2937;
                        margin: 0 0 0.5rem;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }

                    .header-content p {
                        font-size: 0.875rem;
                        color: #6b7280;
                        margin: 0;
                    }

                    /* Tabs */
                    .tab-navigation {
                    
                        margin: 0 auto 1.5rem;
                        display: flex;
                        background: white;
                        border-radius: 1rem;
                        padding: 0.25rem;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    }

                    .tab-button {
                        flex: 1;
                        padding: 0.75rem 1rem;
                        border: none;
                        background: transparent;
                        border-radius: 0.75rem;
                        font-size: 0.875rem;
                        font-weight: 500;
                        color: #6b7280;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        transition: all 0.2s;
                    }

                    .tab-button.active {
                        background: #3b82f6;
                        color: white;
                        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                    }

                    .tab-button:hover:not(.active) {
                        background: #f9fafb;
                    }

                    /* Section Headers */
                    .section-header {
                      
                        margin: 0 auto 1.5rem;
                    }

                    .section-header h2 {
                        font-size: 1.25rem;
                        font-weight: 600;
                        color: #1f2937;
                        margin: 0 0 0.25rem;
                    }

                    .section-header p {
                        font-size: 0.875rem;
                        color: #6b7280;
                        margin: 0;
                    }

                    /* Chapter Cards */
                    .chapters-grid {
                        
                        margin: 0 auto;
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .chapter-card {
                        background: white;
                        border-radius: 1rem;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                        overflow: hidden;
                        transition: all 0.3s;
                    }

                    .chapter-card:hover {
                        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
                        border-color: #cbd5e0;
                    }

                    .chapter-header {
                        padding: 1.25rem 1.5rem;
                        cursor: pointer;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        transition: background-color 0.2s;
                    }

                    .chapter-header:hover {
                        background-color: #f9fafb;
                    }

                    .chapter-title {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex: 1;
                    }

                    .chapter-icon {
                        width: 2.5rem;
                        height: 2.5rem;
                        background: #e0f2fe;
                        color: #0369a1;
                        border-radius: 0.75rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    }

                    .chapter-title h3 {
                        font-size: 1rem;
                        font-weight: 600;
                        color: #1f2937;
                        margin: 0 0 0.25rem;
                    }

                    .chapter-meta {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 0.75rem;
                        color: #6b7280;
                    }

                    .chapter-toggle {
                        color: #9ca3af;
                    }

                    .chapter-stats {
                        padding: 1rem 1.5rem;
                        border-top: 1px solid #f3f4f6;
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 1rem;
                    }

                    .stat-item {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                    }

                    .stat-label {
                        font-size: 0.75rem;
                        color: #6b7280;
                        margin-bottom: 0.125rem;
                    }

                    .stat-value {
                        font-size: 1.125rem;
                        font-weight: 700;
                    }

                    /* Topics Section */
                    .topics-section {
                        padding: 1.5rem;
                        border-top: 1px solid #f3f4f6;
                        background: #f9fafb;
                    }

                    .topics-header {
                        font-size: 0.875rem;
                        font-weight: 600;
                        color: #374151;
                        margin-bottom: 1rem;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }

                    .topics-grid {
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .topic-card {
                        background: white;
                        border-radius: 0.75rem;
                        padding: 1rem;
                        border: 1px solid #e5e7eb;
                    }

                    .topic-name {
                        font-size: 0.875rem;
                        font-weight: 600;
                        color: #1f2937;
                        margin-bottom: 0.75rem;
                    }

                    .topic-stats {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                        gap: 0.75rem;
                    }

                    .topic-stat {
                        display: flex;
                        justify-content: space-between;
                        font-size: 0.75rem;
                    }

                    .topic-stat span {
                        color: #6b7280;
                    }

                    .topic-stat strong {
                        font-weight: 600;
                    }

                    .loading-topics {
                        text-align: center;
                        padding: 1rem;
                        color: #6b7280;
                        font-size: 0.875rem;
                    }

                    .no-topics {
                        text-align: center;
                        padding: 1rem;
                        color: #9ca3af;
                        font-size: 0.875rem;
                    }

                    /* Questions Section */
                    .questions-grid {
                      
                        margin: 0 auto;
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .question-card {
                        background: white;
                        border-radius: 1rem;
                        border: 1px solid #e2e8f0;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                        padding: 1.5rem;
                    }

                    .question-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 1rem;
                    }

                    .question-number {
                        font-size: 1.125rem;
                        font-weight: 700;
                        color: #3b82f6;
                        background: #eff6ff;
                        padding: 0.25rem 0.75rem;
                        border-radius: 0.5rem;
                    }

                    .question-stats {
                        display: flex;
                        gap: 0.5rem;
                        flex-wrap: wrap;
                    }

                    .question-content {
                        font-size: 0.9375rem;
                        line-height: 1.6;
                        color: #1f2937;
                        margin-bottom: 1.5rem;
                        padding-bottom: 1rem;
                        border-bottom: 1px solid #f3f4f6;
                    }

                    .options-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 0.75rem;
                    }

                    .option-card {
                        background: #f9fafb;
                        border: 1px solid #e5e7eb;
                        border-radius: 0.75rem;
                        padding: 1rem;
                        transition: all 0.2s;
                    }

                    .option-card.correct {
                        background: #dcfce7;
                        border-color: #16a34a;
                    }

                    .option-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 0.5rem;
                    }

                    .option-letter {
                        width: 1.75rem;
                        height: 1.75rem;
                        background: #e5e7eb;
                        color: #374151;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 700;
                        font-size: 0.75rem;
                    }

                    .option-card.correct .option-letter {
                        background: #16a34a;
                        color: white;
                    }

                    .correct-badge {
                        display: flex;
                        align-items: center;
                        gap: 0.25rem;
                        font-size: 0.75rem;
                        font-weight: 600;
                        color: #166534;
                        background: rgba(22, 101, 52, 0.1);
                        padding: 0.25rem 0.5rem;
                        border-radius: 0.5rem;
                    }

                    .option-content {
                        font-size: 0.875rem;
                        line-height: 1.5;
                        color: #1f2937;
                    }

                    /* Students Section */
                    .student-tabs {
                        margin: 0 auto 1.5rem;
                        display: flex;
                        gap: 0.5rem;
                        padding: 0.25rem;
                        background: white;
                        border-radius: 1rem;
                        border: 1px solid #e2e8f0;
                    }

                    .student-tab {
                        flex: 1;
                        padding: 0.75rem 1rem;
                        border: none;
                        background: transparent;
                        border-radius: 0.75rem;
                        font-size: 0.875rem;
                        font-weight: 500;
                        color: #6b7280;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        transition: all 0.2s;
                    }

                    .student-tab.active {
                        background: #3b82f6;
                        color: white;
                        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                    }

                    .student-tab:hover:not(.active) {
                        background: #f9fafb;
                    }

                    .students-table-container {
                        margin: 0 auto;
                        background: white;
                        border-radius: 1rem;
                        border: 1px solid #e2e8f0;
                        overflow: hidden;
                        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    }

                    .students-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 0.875rem;
                    }

                    .students-table thead {
                        background: #f9fafb;
                    }

                    .students-table th {
                        padding: 1rem 1.25rem;
                        text-align: left;
                        font-weight: 600;
                        color: #374151;
                        border-bottom: 2px solid #e5e7eb;
                    }

                    .students-table td {
                        padding: 1rem 1.25rem;
                        border-bottom: 1px solid #f3f4f6;
                        vertical-align: middle;
                    }

                    .students-table tbody tr:hover {
                        background: #f9fafb;
                    }

                    .rank-badge {
                        width: 1.75rem;
                        height: 1.75rem;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 700;
                        font-size: 0.75rem;
                    }

                    .top-rank {
                        background: #3b82f6;
                        color: white;
                    }

                    .weak-rank {
                        background: #ef4444;
                        color: white;
                    }

                    .student-name-cell {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        font-weight: 500;
                        color: #1f2937;
                    }

                    .student-avatar {
                        width: 1.75rem;
                        height: 1.75rem;
                        background: #e0f2fe;
                        color: #0369a1;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .roll-number {
                        color: #6b7280;
                    }

                    .accuracy-badge {
                        display: inline-block;
                        padding: 0.25rem 0.5rem;
                        border-radius: 0.75rem;
                        font-size: 0.75rem;
                        font-weight: 600;
                        min-width: 4rem;
                        text-align: center;
                    }

                    .high-accuracy {
                        background: #dcfce7;
                        color: #166534;
                    }

                    .low-accuracy {
                        background: #fee2e2;
                        color: #991b1b;
                    }

                    .marks-display {
                        font-weight: 600;
                        color: #1f2937;
                    }

                    .view-student-button {
                        padding: 0.375rem 0.75rem;
                        background: #3b82f6;
                        color: white;
                        border: none;
                        border-radius: 0.5rem;
                        font-size: 0.75rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s;
                    }

                    .view-student-button:hover {
                        background: #2563eb;
                        transform: translateY(-1px);
                    }

                    /* Badge Component */
                    .badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 0.375rem;
                        padding: 0.375rem 0.75rem;
                        border-radius: 0.75rem;
                        font-size: 0.75rem;
                        font-weight: 600;
                    }

                    /* Empty States */
                    .empty-state {
                        text-align: center;
                        padding: 3rem 2rem;
                        color: #6b7280;
                        font-size: 0.875rem;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 0.75rem;
                    }

                    /* Color Utilities */
                    .text-green-600 { color: #16a34a; }
                    .text-red-600 { color: #dc2626; }
                    .text-blue-600 { color: #0369a1; }
                    .text-gray-600 { color: #6b7280; }

                    /* Responsive Design */
                    @media (max-width: 768px) {
                        .analysis-container {
                            padding: 1rem;
                        }

                        .tab-navigation, .student-tabs {
                            flex-direction: column;
                        }

                        .chapter-stats {
                            grid-template-columns: 1fr;
                        }

                        .options-grid {
                            grid-template-columns: 1fr;
                        }

                        .students-table {
                            display: block;
                            overflow-x: auto;
                        }

                        .students-table th,
                        .students-table td {
                            padding: 0.75rem;
                        }
                    }

                    @media (max-width: 480px) {
                        .question-header {
                            flex-direction: column;
                            gap: 0.75rem;
                            align-items: flex-start;
                        }

                        .question-stats {
                            width: 100%;
                            justify-content: center;
                        }

                        .badge {
                            flex: 1;
                            justify-content: center;
                        }
                    }
                `}
            </style>
        </div>
    );
};

const Badge = ({ icon, label, value, color, bg }) => (
    <div className="badge" style={{ background: bg, color }}>
        {icon}
        <span>{label}</span>
        <span style={{
            background: 'rgba(255, 255, 255, 0.7)',
            padding: '0.125rem 0.5rem',
            borderRadius: '0.5rem',
            fontWeight: '700'
        }}>
            {value}
        </span>
    </div>
);

export default ChapterAnalysis;